import { useApolloClient } from "@apollo/client/react";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Dialog } from "@/components/common/Dialog";
import { Button } from "@/components/design/Button";
import { DocumentLibrary } from "@/components/documents/DocumentLibrary";
import { StudioDocumentTextDocument } from "@/graphql/generated/operations";
import { ANALYZABLE_ACCEPT, isAnalyzableDocument } from "@/lib/analyzable-documents";
import { userErrorMessage } from "@/lib/user-error";
import { toDocumentItem, useMyDocuments, useUploadMyDocuments } from "@/pages/save/service/use-my-documents";
import type { StudioDocument } from "./notebook-markdown";

/**
 * Brings one of the user's documents into the notebook: the documents library of Save · Documents, with the
 * same list, folders and upload, limited to what the documents extraction can read. Choosing a document (or
 * uploading one, which files it in the documents first) reads its text through genesis `documentText` (OCR
 * or text extraction, cached per document) and hands it on; nothing is added before the text is there.
 */
export function StudioDocumentPicker({ open, onClose, onBring }: {
  open: boolean;
  onClose: () => void;
  onBring: (document: StudioDocument) => Promise<boolean>;
}) {
  const intl = useIntl();
  const client = useApolloClient();
  const { docs, loading, draining, error, paginationError, retryPagination, refetch } = useMyDocuments();
  const readable = useMemo(() => docs.filter((d) => isAnalyzableDocument(d.fileName ?? d.name ?? "")), [docs]);
  const items = useMemo(() => readable.map((d) => toDocumentItem(d, intl)), [readable, intl]);
  const storedNames = useMemo(() => docs.map((d) => ({ id: d.id, name: d.name ?? "" })), [docs]);
  const [message, setMessage] = useState<string | null>(null);
  // What an upload held back or failed on, kept apart from `message`: bringing in the rest must not clear it.
  const [uploadNotice, setUploadNotice] = useState<string | null>(null);
  const { upload, busy: uploading } = useUploadMyDocuments({ storedNames, onError: setUploadNotice });
  const [reading, setReading] = useState<string | null>(null);
  const [brought, setBrought] = useState<string | null>(null);

  const bring = async (documentId: string, name: string, stayOpen = false) => {
    setReading(name);
    setMessage(null);
    setBrought(null);
    try {
      const { data } = await client.query({ query: StudioDocumentTextDocument, variables: { id: documentId }, fetchPolicy: "network-only" });
      const text = (data?.documentText.pages ?? []).map((page) => page.markdown.trim()).filter(Boolean).join("\n\n");
      if (!text) {
        setMessage(intl.formatMessage({ id: "studio.document.noText", defaultMessage: "No text could be read from this document." }));
        return;
      }
      if (await onBring({ documentId, name, text })) {
        if (stayOpen) setBrought(name);
        else onClose();
      }
    } catch (failure) {
      setMessage(userErrorMessage(intl, failure));
    } finally {
      setReading(null);
    }
  };

  const busy = uploading || reading !== null;
  const problem = message ?? paginationError ?? (error ? userErrorMessage(intl, error) : null);
  const alerts = [uploadNotice, problem].filter((alert): alert is string => alert !== null);
  return (
    <Dialog
      id="studio-document-picker"
      open={open}
      onClose={onClose}
      preventClose={busy}
      maxWidth="lg"
      title={<FormattedMessage id="studio.document.pickTitle" defaultMessage="Bring in a document" />}
    >
      <DocumentLibrary
        scopeId="studio-documents"
        idPrefix="studio-documents"
        embedded
        items={items}
        description={<FormattedMessage id="studio.document.pickHint" defaultMessage="From your documents. Its text is read (OCR for PDFs and images) and goes into the notebook as context." />}
        loading={loading || draining}
        busy={busy}
        accept={ANALYZABLE_ACCEPT}
        onOpen={(id) => bring(id, readable.find((d) => d.id === id)?.name ?? "")}
        onUpload={async (files, groupId) => {
          // Counted before the first await: the file input empties its live FileList right after handing it over.
          const offered = files.length;
          const created = await upload(files, groupId);
          if (!created.length) return;
          await refetch();
          // Whatever did not make it is named in the upload notice, and closing would take the notice along.
          await bring(created[0]!.id, created[0]!.name, created.length < offered);
        }}
        error={
          reading ? (
            <p role="status" className="mt-3 smycles-field-meta text-ink">
              <FormattedMessage id="studio.document.reading" defaultMessage="Reading “{name}” …" values={{ name: reading }} />
            </p>
          ) : brought || alerts.length ? (
            <>
              {brought ? (
                <p role="status" className="mt-3 smycles-field-meta text-ink">
                  <FormattedMessage id="studio.document.brought" defaultMessage="“{name}” is in the notebook." values={{ name: brought }} />
                </p>
              ) : null}
              {alerts.map((alert) => (
                <p key={alert} role="alert" className="mt-3 smycles-field-meta text-ink">{alert}</p>
              ))}
              {paginationError ? (
                <Button variant="secondary" size="sm" className="mt-2" onClick={retryPagination}>
                  <FormattedMessage id="common.tryAgain" defaultMessage="Try again" />
                </Button>
              ) : null}
            </>
          ) : null
        }
      />
    </Dialog>
  );
}
